import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { formatCurrencyPtBr } from "../../../../utils/numberFormat";
import { useItensTabela, useExcluirItemTabela } from "../hooks/useTabelaProcedimento";
import ItemTabelaModal from "./ItemTabelaModal";

const TabelaProcedimentoItensList = ({ tabelaId, somenteLeitura = false }) => {
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const cancelRef = useRef();

  const { isOpen: isItemOpen, onOpen: onItemOpen, onClose: onItemClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const excluirItem = useExcluirItemTabela(tabelaId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useItensTabela(tabelaId, { page, limit, q });
  const itens = data?.itens || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const handleNovoClick = () => {
    setEditingItem(null);
    onItemOpen();
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    onItemOpen();
  };

  const handleDeleteClick = (id) => {
    setDeleteItemId(id);
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (!deleteItemId) return;
    excluirItem.mutate(deleteItemId, { onSuccess: onDeleteClose });
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={4}>
        <InputGroup maxW="320px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Buscar por código ou título..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </InputGroup>
        {!somenteLeitura && (
          <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleNovoClick}>
            Novo procedimento
          </Button>
        )}
      </Flex>

      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Código</Th>
            <Th>Título</Th>
            <Th isNumeric>Valor</Th>
            {!somenteLeitura && <Th textAlign="right">Ações</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {!isLoading && itens.length === 0 && (
            <Tr>
              <Td colSpan={somenteLeitura ? 3 : 4}>
                <Text color="gray.500">Nenhum procedimento encontrado.</Text>
              </Td>
            </Tr>
          )}
          {itens.map((item) => (
            <Tr key={item._id}>
              <Td>{item.codigo}</Td>
              <Td>{item.titulo}</Td>
              <Td isNumeric>{formatCurrencyPtBr(item.valor)}</Td>
              {!somenteLeitura && (
                <Td textAlign="right">
                  <HStack justify="flex-end">
                    <IconButton
                      aria-label="Editar"
                      icon={<EditIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditClick(item)}
                    />
                    <IconButton
                      aria-label="Excluir"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDeleteClick(item._id)}
                    />
                  </HStack>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Flex justify="space-between" align="center" mt={4}>
        <HStack>
          <Text fontSize="sm">Itens por página:</Text>
          <Select
            size="sm"
            w="80px"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
          <Text fontSize="sm" color="gray.500">
            {total} resultado(s)
          </Text>
        </HStack>
        <HStack>
          <Button size="sm" onClick={() => setPage(1)} isDisabled={page <= 1}>
            Primeira
          </Button>
          <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1}>
            Anterior
          </Button>
          <Text fontSize="sm">
            Página {page} de {totalPages}
          </Text>
          <Button size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} isDisabled={page >= totalPages}>
            Próxima
          </Button>
          <Button size="sm" onClick={() => setPage(totalPages)} isDisabled={page >= totalPages}>
            Última
          </Button>
        </HStack>
      </Flex>

      {!somenteLeitura && (
        <>
          <ItemTabelaModal isOpen={isItemOpen} onClose={onItemClose} tabelaId={tabelaId} item={editingItem} />
          <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Excluir procedimento
                </AlertDialogHeader>
                <AlertDialogBody>Tem certeza que deseja excluir este procedimento?</AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onDeleteClose}>
                    Cancelar
                  </Button>
                  <Button colorScheme="red" onClick={handleConfirmDelete} ml={3} isLoading={excluirItem.isPending}>
                    Excluir
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      )}
    </Box>
  );
};

export default TabelaProcedimentoItensList;
